import { useState } from "react";
import { Bell, CheckCheck, Calendar, MessageCircle, AlertCircle, CheckCircle2, Clock, Megaphone } from "lucide-react";
import { useNotificationsData, type AppNotification } from "@/hooks/useNotificationsData";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import NotificationDetailDialog from "@/components/NotificationDetailDialog";

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; route: string }> = {
  booking_accepted: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", route: "/mes-reservations" },
  booking_refused: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", route: "/mes-reservations" },
  booking_new: { icon: <Calendar className="w-4 h-4" />, color: "text-primary", route: "/pro/dashboard" },
  booking_cancelled: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", route: "/mes-reservations" },
  booking_validated: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", route: "/mes-reservations" },
  care_request_new: { icon: <Megaphone className="w-4 h-4" />, color: "text-primary", route: "/demandes" },
  care_request_response: { icon: <Megaphone className="w-4 h-4" />, color: "text-primary", route: "/demandes" },
  new_message: { icon: <MessageCircle className="w-4 h-4" />, color: "text-blue-600", route: "/messages" },
  admin_message: { icon: <MessageCircle className="w-4 h-4" />, color: "text-primary", route: "/messages" },
  account_suspended: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", route: "/settings" },
  account_reactivated: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", route: "/settings" },
};

const resolveRoute = (notif: AppNotification, fallback: string): string => {
  const meta = (notif.metadata ?? {}) as Record<string, unknown>;
  const bookingId = typeof meta.booking_id === "string" ? meta.booking_id : null;
  const conversationId = typeof meta.conversation_id === "string" ? meta.conversation_id : null;
  const requestId = typeof meta.request_id === "string" ? meta.request_id : null;

  if (notif.type.startsWith("booking_") && bookingId) return `/mes-reservations?booking=${bookingId}`;
  if ((notif.type === "new_message" || notif.type === "admin_message") && conversationId) return `/messages?conversation=${conversationId}`;
  if (notif.type.startsWith("care_request") && requestId) return `/demandes?request=${requestId}`;
  return fallback;
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

const NotificationItem = ({
  notif,
  onRead,
  onOpen,
}: {
  notif: AppNotification;
  onRead: (id: string) => void;
  onOpen: (notif: AppNotification) => void;
}) => {
  const config = TYPE_CONFIG[notif.type] ?? { icon: <Bell className="w-4 h-4" />, color: "text-muted-foreground", route: "/" };

  const handleClick = () => {
    if (!notif.read) onRead(notif.id);
    onOpen(notif);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "w-full text-left px-3 py-2.5 rounded-lg transition-colors flex gap-3 items-start",
        notif.read ? "hover:bg-muted/50" : "bg-primary/5 hover:bg-primary/10"
      )}
    >
      <div className={cn("mt-0.5 shrink-0", config.color)}>{config.icon}</div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className={cn("text-sm leading-tight", !notif.read && "font-semibold")}>
          {notif.title}
        </p>
        {notif.body && (
          <p className="text-xs text-muted-foreground line-clamp-2">{notif.body}</p>
        )}
        <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {timeAgo(notif.created_at)}
        </p>
      </div>
      {!notif.read && (
        <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
      )}
    </button>
  );
};

const NotificationBell = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotificationsData();
  const navigate = useNavigate();
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; notif: AppNotification | null }>({ open: false, notif: null });

  const handleOpen = (notif: AppNotification) => {
    setDetailDialog({ open: true, notif });
  };


  return (
    <>
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted transition-colors" aria-label="Notifications">
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="text-sm font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={markAllAsRead}
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Tout lire
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-10 text-center">
              <Bell className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            <div className="p-1.5 space-y-0.5">
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notif={n}
                  onRead={markAsRead}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="border-t border-border p-2">
          <button
            onClick={() => navigate("/notifications")}
            className="w-full text-center text-xs font-semibold text-primary hover:bg-primary/5 py-2 rounded-md transition-colors"
          >
            Voir toutes les notifications
          </button>
        </div>
      </PopoverContent>
    </Popover>

    <NotificationDetailDialog
      open={detailDialog.open}
      onOpenChange={(open) => setDetailDialog((s) => ({ ...s, open }))}
      notification={detailDialog.notif}
    />
    </>
  );
};

export default NotificationBell;
